import React from "react"
import PropTypes from "prop-types"
import { Alert, Card, Flex } from "antd"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"

import { Text, Title } from "ui/Typography"
import styles from "./BookingForm.module.css"

const formatSchedule = (date, time) => {
  if (!date) return null
  const formattedDate = dayjs(date).isValid() ? dayjs(date).format("MMM D, YYYY") : date
  return [formattedDate, time].filter(Boolean).join(" · ")
}

/**
 * DS-4473. Shown at the top of the edit screen when recovering a booking request that expired
 * before it could be accepted. It carries what the form below cannot: why the request is in this
 * state, what the guest originally asked for, and which card already paid — recovery reconciles an
 * existing payment rather than collecting a fresh deposit. The new schedule is not repeated here;
 * the guide is picking it in the fields directly below.
 */
const RecoverySummary = ({ previousDate, previousTime, cardBrand, cardLast4, recoveryDeadline }) => {
  const { t } = useTranslation()

  const previousSchedule = formatSchedule(previousDate, previousTime)
  const hasCard = Boolean(cardBrand || cardLast4)
  const deadlineLabel =
    recoveryDeadline && dayjs(recoveryDeadline).isValid()
      ? t("booking.recovery.deadline", { deadline: dayjs(recoveryDeadline).format("MMM D, YYYY h:mm A") })
      : null

  return (
    <Card className={styles.cardContainer}>
      <Alert type="warning" showIcon message={t("booking.recovery.expired_banner")} description={deadlineLabel} />

      <Title className={styles.cardTitle} level={5}>
        {t("booking.recovery.review_title")}
      </Title>

      <Flex vertical gap={8}>
        {previousSchedule && (
          <Flex justify="space-between" gap={12}>
            <Text type="secondary">{t("booking.recovery.previous_request")}</Text>
            <Text>{previousSchedule}</Text>
          </Flex>
        )}
        <Flex justify="space-between" gap={12}>
          <Text type="secondary">{t("booking.payment_method")}</Text>
          {/* Brand and last four only — never anything that could identify the full card. */}
          <Text>{hasCard ? t("booking.recovery.card_on_file", { brand: cardBrand || "Card", last4: cardLast4 || "••••" }) : t("booking.recovery.no_card_on_file")}</Text>
        </Flex>
        {/* The card line above must not read as "this will be charged on submit" — nor as a promise
            that nothing can be, since a price change opens the payment step where the guide may
            choose to collect now. The note names both outcomes and where the choice is made. */}
        <Text type="secondary">{t("booking.recovery.card_on_file_note")}</Text>
      </Flex>

      <Text type="secondary" className="mt-xs">
        {t("booking.recovery.locked_notice")}
      </Text>
    </Card>
  )
}

RecoverySummary.propTypes = {
  previousDate: PropTypes.string,
  previousTime: PropTypes.string,
  cardBrand: PropTypes.string,
  cardLast4: PropTypes.string,
  recoveryDeadline: PropTypes.string
}

export default RecoverySummary
